import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import salesStatsService from "../../Services/salesStatsService";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const CATEGORIES = [
  { label: "Patient", keyAmount: "patientAmount", keySales: "patientSales" },
  { label: "Medicines", keyAmount: "medicinesAmount", keySales: "medicinesSales" },
  { label: "Services", keyAmount: "servicesAmount", keySales: "servicesSales" },
  { label: "Pharmacy", keyAmount: "pharmacyAmount", keySales: "pharmacySales" },
  { label: "Radiology", keyAmount: "radiologyAmount", keySales: "radiologySales" },
  { label: "Laboratory", keyAmount: "laboratoryAmount", keySales: "laboratorySales" },
];

// Date | (Sales, Amount) x categories | Total
const colWidths = [
  "110px",
  ...CATEGORIES.flatMap(() => ["48px", "90px"]),
  "95px",
];
const tableMinWidth = colWidths.reduce((sum, w) => sum + parseInt(w, 10), 0);

const ColGroup = () => (
  <colgroup>
    {colWidths.map((w, i) => (
      <col key={i} style={{ width: w, minWidth: w }} />
    ))}
  </colgroup>
);

/* ---------- data helpers ---------- */

// Response se list nikalta hai: pehle di gayi keys, warna pehla array
const pickList = (res, keys = []) => {
  for (const key of keys) {
    if (Array.isArray(res?.[key])) return res[key];
  }
  return Object.values(res || {}).find((v) => Array.isArray(v)) || [];
};

// Ek API call safe tareeqe se: fail ho ya method na ho to [] return
const safeFetch = async (methodName, keys) => {
  const fn = salesStatsService?.[methodName];
  if (typeof fn !== "function") {
    console.warn(`salesStatsService.${methodName} exist nahi karta`);
    return [];
  }
  try {
    const res = await fn.call(salesStatsService);
    return pickList(res, keys);
  } catch (error) {
    console.error(`${methodName} fail hua:`, error);
    return [];
  }
};

// Record ki date ("YYYY-MM-DD")
const dayOf = (item) => {
  const raw =
    item?.new_date ??
    item?.sale_date ??
    item?.date ??
    item?.created_at;

  return raw ? String(raw).split("T")[0] : undefined;
};

const toMap = (list) =>
  Object.fromEntries(list.map((item) => [dayOf(item), item]));

const Income = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const [patientData, setPatientData] = useState([]);
  const [medicinesData, setMedicinesData] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [pharmacyData, setPharmacyData] = useState([]);
  const [radiologyData, setRadiologyData] = useState([]);
  const [laboratoryData, setLaboratoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [expandedDays, setExpandedDays] = useState({});

  /* ---------- helpers ---------- */

  const formatDateLabel = (dateInput) => {
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const fmt = (n) => {
    const v = isNaN(n) ? 0 : Math.round(parseFloat(n) * 100) / 100;
    return v.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const calculateTotal = (data, key) =>
    data.reduce((sum, item) => sum + parseFloat(item[key] || 0), 0);

  const getRowTotal = (item) =>
    CATEGORIES.reduce((sum, c) => sum + Number(item[c.keyAmount] || 0), 0);

  /* ---------- data fetching ---------- */

  useEffect(() => {
  const fetchSalesData = async () => {
    try {
      setLoading(true);

      const [
        patient,
        medicines,
        services,
        pharmacy,
        radiologyResponse,
        laboratoryResponse,
      ] = await Promise.all([
        safeFetch("fetchDailyPatientStats", ["daysales"]),
        safeFetch("fetchDailyMedicineStats", ["daymidicinesales"]),
        safeFetch("fetchDailyServicesStats", ["dayservicesales"]),
        safeFetch("fetchDailyPharmacyStats", ["daypharmacysales"]),

        salesStatsService.fetchDailyRadiologyStats(),

        salesStatsService.fetchDailyLaboratoryStats(),
      ]);

      setPatientData(patient);
      setMedicinesData(medicines);
      setServicesData(services);
      setPharmacyData(pharmacy);

      // Radiology API response
      const radiologyRecords =
        radiologyResponse?.dayradiologysales || [];

      setRadiologyData(
        radiologyRecords.map((item) => ({
          ...item,
          date: dayOf(item),
          total_sales: Number(item.total_rad_sales) || 0,
          total_amount: Number(item.total_recieved) || 0,
        }))
      );

      // Laboratory API response
      const laboratoryRecords =
        laboratoryResponse?.daylabsales || [];

      setLaboratoryData(
        laboratoryRecords.map((item) => ({
          ...item,
          date: dayOf(item),
          total_sales: Number(item.total_lab_orders) || 0,
          total_amount: Number(item.total_fee_recieved) || 0,
        }))
      );

    } catch (error) {
      console.error("Error fetching income statistics:", error);
      toast.error("Error fetching statistics");
    } finally {
      setLoading(false);
    }
  };

  fetchSalesData();
}, []);

  /* ---------- combine ---------- */

  const patientsMap = toMap(patientData);
  const medicinesMap = toMap(medicinesData);
  const servicesMap = toMap(servicesData);
  const pharmacyMap = toMap(pharmacyData);
  const radiologyMap = toMap(radiologyData);
  const laboratoryMap = toMap(laboratoryData);

  const allDates = new Set(
    [
      ...patientData,
      ...medicinesData,
      ...servicesData,
      ...pharmacyData,
      ...radiologyData,
      ...laboratoryData,
    ]
      .map(dayOf)
      .filter(Boolean)
  );

  const monthDateKeys = Array.from(allDates)
    .filter((date) => {
      const d = new Date(date);
      return (
        d.getMonth() + 1 === Number(selectedMonth) &&
        d.getFullYear() === Number(selectedYear)
      );
    })
    .sort();

  const combinedData = monthDateKeys.map((dateKey) => {
    const patient = patientsMap[dateKey] || {};
    const medicine = medicinesMap[dateKey] || {};
    const service = servicesMap[dateKey] || {};
    const pharmacy = pharmacyMap[dateKey] || {};
    const radiology = radiologyMap[dateKey] || {};
    const laboratory = laboratoryMap[dateKey] || {};

    return {
      dateKey,
      date: formatDateLabel(new Date(dateKey)),

      patientSales: Number(patient.total_patients || 0),
      patientAmount: Number(patient.total_fee || 0),

      medicinesSales: Number(medicine.total_sales || 0),
      medicinesAmount: Number(medicine.total_amount || 0),

      servicesSales: Number(service.total_sales || 0),
      servicesAmount: Number(service.total_amount || 0),

      pharmacySales: Number(pharmacy.total_sales || 0),
      pharmacyAmount: Number(pharmacy.total_amount || 0),

      radiologySales: Number(radiology.total_sales || 0),
      radiologyAmount: Number(radiology.total_amount || 0),

      laboratorySales: Number(laboratory.total_sales || 0),
      laboratoryAmount: Number(laboratory.total_amount || 0),
    };
  });

  const grandTotal = CATEGORIES.reduce(
    (sum, c) => sum + calculateTotal(combinedData, c.keyAmount),
    0
  );

  const toggleDay = (dateKey) => {
    setExpandedDays((prev) => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  /* ---------- UI ---------- */

  return (
    <div className="p-3 sm:p-4 font-sans">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs font-medium tracking-widest uppercase text-gray-500">
          Income Summary
        </p>
        <div className="flex gap-2">
          <select
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {MONTHS.map((name, i) => (
              <option key={i} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
          <select
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {[2024, 2025, 2026].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2 sm:gap-3 mb-4">
        {CATEGORIES.map(({ label, keyAmount, keySales }) => (
          <div
            key={label}
            className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5"
          >
            <p className="text-[10px] tracking-wide text-gray-400 uppercase mb-1">
              {label}
            </p>
            <p className="text-base font-semibold text-gray-800">
              {fmt(calculateTotal(combinedData, keyAmount))}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">
              {calculateTotal(combinedData, keySales)} sales
            </p>
          </div>
        ))}
        <div className="bg-gray-100 rounded-lg px-3 py-2.5 border-t-2 border-[#F07F1C] col-span-2 sm:col-span-3 lg:col-span-2 xl:col-span-1">
          <p className="text-[10px] tracking-wide text-gray-400 uppercase mb-1">
            Grand Total
          </p>
          <p className="text-base font-bold text-[#F07F1C]">{fmt(grandTotal)}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {combinedData.length} days
          </p>
        </div>
      </div>

      {/* ══════════ MOBILE VIEW (< 640px) ══════════ */}
      <div className="block sm:hidden">
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-xs">Loading...</div>
        ) : (
          <>
            <div className="space-y-2">
              {combinedData.map((item) => {
                const rowTotal = getRowTotal(item);
                const isOpen = !!expandedDays[item.dateKey];
                const hasData = rowTotal > 0;

                return (
                  <div
                    key={item.dateKey}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => toggleDay(item.dateKey)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 text-left"
                    >
                      <span className="text-[12px] font-medium text-gray-700">
                        {item.date}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[13px] font-semibold ${
                            hasData ? "text-[#F07F1C]" : "text-gray-300"
                          }`}
                        >
                          {fmt(rowTotal)}
                        </span>
                        <svg
                          className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-3 py-2 bg-white divide-y divide-gray-100">
                        {CATEGORIES.map(({ label, keyAmount, keySales }) => (
                          <div
                            key={label}
                            className="flex items-center justify-between py-2"
                          >
                            <span className="text-[12px] text-gray-500 w-20">
                              {label}
                            </span>
                            <div className="flex items-center gap-4">
                              <span className="text-[11px] text-gray-400 w-12 text-right">
                                {item[keySales]} sales
                              </span>
                              <span className="text-[12px] font-medium text-gray-800 w-20 text-right">
                                {fmt(item[keyAmount])}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Mobile Grand Total Footer */}
            <div className="mt-3 bg-[#215187] rounded-xl px-3 py-3">
              {CATEGORIES.map(({ label, keyAmount, keySales }) => (
                <div
                  key={label}
                  className="flex items-center justify-between py-1.5 border-b border-white/10 last:border-0"
                >
                  <span className="text-[12px] text-gray-300">{label}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-[11px] text-gray-300 w-14 text-right">
                      {calculateTotal(combinedData, keySales)} sales
                    </span>
                    <span className="text-[12px] text-gray-100 font-medium w-20 text-right">
                      {fmt(calculateTotal(combinedData, keyAmount))}
                    </span>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 mt-1 border-t border-white/20">
                <span className="text-[12px] font-medium text-gray-100">
                  Grand Total
                </span>
                <span className="text-[14px] font-bold text-[#F07F1C]">
                  {fmt(grandTotal)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ══════════ DESKTOP VIEW (>= 640px) ══════════ */}
      <div className="hidden sm:block border rounded-xl overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: "520px" }}>
          <table
            className="w-full text-sm"
            style={{
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: 0,
              minWidth: tableMinWidth,
            }}
          >
            <ColGroup />

            {/* STICKY HEADER */}
            <thead>
              <tr>
                <th className="sticky top-0 z-20 bg-[#215187] text-left px-3 py-2.5 text-[11px] text-gray-100 uppercase border-r border-white/10">
                  Date
                </th>
                {CATEGORIES.map(({ label }) => (
                  <th
                    key={label}
                    colSpan={2}
                    className="sticky top-0 z-20 bg-[#215187] text-center px-2 py-2.5 text-[11px] text-gray-100 uppercase border-r border-white/10"
                  >
                    {label}
                  </th>
                ))}
                <th className="sticky top-0 z-20 bg-[#215187] text-right px-3 py-2.5 text-[11px] text-gray-100 uppercase">
                  Total
                </th>
              </tr>
              <tr>
                <th className="sticky top-[37px] z-20 bg-gray-100 border-b border-r border-gray-200 py-1.5"></th>
                {CATEGORIES.flatMap(({ label }) => [
                  <th
                    key={`${label}-s`}
                    className="sticky top-[37px] z-20 bg-gray-100 text-gray-500 text-center px-2 py-1.5 text-[11px] border-b border-r border-gray-200 font-normal"
                  >
                    Sales
                  </th>,
                  <th
                    key={`${label}-a`}
                    className="sticky top-[37px] z-20 bg-gray-100 text-gray-500 text-center px-2 py-1.5 text-[11px] border-b border-r border-gray-200 font-normal"
                  >
                    Amount
                  </th>,
                ])}
                <th className="sticky top-[37px] z-20 bg-gray-100 border-b border-gray-200 py-1.5"></th>
              </tr>
            </thead>

            {/* BODY */}
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={colWidths.length}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : combinedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={colWidths.length}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    No records found for this period.
                  </td>
                </tr>
              ) : (
                combinedData.map((item, index) => (
                  <tr
                    key={item.dateKey}
                    className={`border-b border-gray-100 hover:bg-green-50 transition-colors ${
                      index % 2 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <td className="px-3 py-1.5 text-gray-400 text-[12px] border-r border-gray-100">
                      {item.date}
                    </td>
                    {CATEGORIES.flatMap(({ label, keyAmount, keySales }) => [
                      <td
                        key={`${label}-s`}
                        className="px-2 py-1.5 text-center border-r border-gray-100 text-[12px]"
                      >
                        {item[keySales]}
                      </td>,
                      <td
                        key={`${label}-a`}
                        className="px-2 py-1.5 text-right border-r border-gray-100 text-[12px]"
                      >
                        {fmt(item[keyAmount])}
                      </td>,
                    ])}
                    <td className="px-3 py-1.5 text-right text-[12px] font-medium">
                      {fmt(getRowTotal(item))}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {/* STICKY FOOTER */}
            <tfoot>
              <tr>
                <td className="sticky bottom-0 z-20 bg-[#215187] text-gray-100 px-3 py-2.5 font-medium text-[12px] border-r border-white/10 border-t">
                  Grand Total
                </td>
                {CATEGORIES.flatMap(({ label, keyAmount, keySales }) => [
                  <td
                    key={`${label}-s`}
                    className="sticky bottom-0 z-20 bg-[#215187] text-gray-100 px-2 py-2.5 text-center text-[12px] border-r border-white/10 border-t"
                  >
                    {calculateTotal(combinedData, keySales)}
                  </td>,
                  <td
                    key={`${label}-a`}
                    className="sticky bottom-0 z-20 bg-[#215187] text-gray-100 px-2 py-2.5 text-right text-[12px] border-r border-white/10 border-t"
                  >
                    {fmt(calculateTotal(combinedData, keyAmount))}
                  </td>,
                ])}
                <td className="sticky bottom-0 z-20 bg-[#215187] text-gray-100 px-3 py-2.5 text-right text-[12px] font-semibold border-t border-white/10">
                  {fmt(grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Income;